import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Colors } from '.';
import { clamp } from '../CodesThatsGottaGoSomewhereElse';
import { Slider, SliderProps } from './Slider';

const Frame = styled.div`
    position: relative;
    display: flex;
    flex-direction: row;
    width: 100%;
`;

const InputWrapper = styled.div`
    flex: 0 0 auto;
    margin-right: .5rem;
`;

const Input = styled.input`
    display: inline-block;
    width: 2rem;
    background: ${Colors.primaryHover};
    color: ${Colors.primaryText};
    padding: .125rem;
    margin-right: .125rem;
    border-radius: 3px;
    font-size: .9rem;
    text-align: right;

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0; /* <-- Apparently some margin are still there even though it's hidden */
    }

    -moz-appearance: textfield;
`;

const Unit = styled.div`
    display: inline-block;
    font-weight: 450;
    color: ${Colors.primaryText};
    font-size: .9rem;
`;

const SliderWrapper = styled.div`
    flex: 1 1 auto;
    display: flex;
    align-items: center;

    & > * {
        flex: 1 1 auto;
    }
`;

export type SliderWithInputProps = SliderProps & { unit?: string };

export const SliderWithInput = ({ unit, ...rest }: SliderWithInputProps) => {
    // eslint-disable-next-line prefer-const
    let { onChange, value, min, max, inc } = rest;
    min = min ?? 0;
    max = max ?? 100;
    inc = inc ?? 1;

    const input = useRef<HTMLInputElement>();
    const [text, setText] = useState('');

    const onSet = useCallback((next: number) => {
        next = Math.min(max, Math.max(min, clamp(next, inc)));
        if (next !== value) onChange(next);
    }, [min, max, inc, value, onChange]);

    const onInputChange = useCallback((e: any) => {
        setText(e.target.value);
        onSet(parseInt(e.target.value, 10) || 0);
    }, [onChange]);

    const onInputBlur = useCallback(() => {
        setText(value?.toString() ?? '');
    }, [value]);

    useEffect(() => {
        if (input.current.matches(':focus')) return;
        setText(value?.toString() ?? '');
    }, [input.current, value]);

    return <Frame>
        <InputWrapper>
            <Input ref={input} type="number" value={text} onChange={onInputChange} onBlur={onInputBlur} />
            {unit && <Unit>{unit}</Unit>}
        </InputWrapper>
        <SliderWrapper>
            <Slider {...rest} />
        </SliderWrapper>
    </Frame>;
};
